import { TIMEOUTS } from '../config/constants'
import { clearDeviceId } from './deviceId'

const BASE_URL = import.meta.env.VITE_API_URL as string | undefined
if (!BASE_URL) {
  throw new Error('Missing required environment variable: VITE_API_URL')
}

// Clean up legacy localStorage tokens from pre-cookie auth
localStorage.removeItem('token')
localStorage.removeItem('refresh_token')

/**
 * Pick the right login page to redirect to. Admins live on /staff/*,
 * operators on /app. On 401 we send them back to their own login.
 */
function loginRedirectTarget(): string {
  return window.location.pathname.startsWith('/staff') ? '/staff/login' : '/'
}

/**
 * Map an AbortError (DOMException OR a value with name==='AbortError') to a
 * friendly 'Request timed out' error so it never reaches global
 * onunhandledrejection. Anything else is re-thrown.
 *
 * Sentry PIXELLAB-WEB-3 surfaced these as unhandled rejections on
 * /staff/calls — the abort fired during res.json() body read (caller code),
 * not during await fetch, so fetchWithTimeout's catch never saw them.
 */
function rethrowAsTimeoutIfAbort(err: unknown): never {
  const isAbort =
    (err instanceof DOMException && err.name === 'AbortError') ||
    (err != null && typeof err === 'object' && 'name' in err && (err as { name?: string }).name === 'AbortError')
  if (isAbort) throw new Error('Request timed out')
  throw err
}

/**
 * Fetch with AbortController timeout. Throws 'Request timed out' on timeout.
 * Timeout covers headers AND body reads (callers may do res.json() / .text()),
 * so the timer is owned by the caller and cleared via the returned dispose().
 * Abort on a fully consumed response is a no-op.
 */
async function fetchWithTimeout(
  url: string,
  options: RequestInit = {},
): Promise<{ res: Response; dispose: () => void }> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), TIMEOUTS.REQUEST_MS)
  const dispose = () => clearTimeout(timer)
  try {
    const res = await fetch(url, { ...options, signal: controller.signal })
    return { res, dispose }
  } catch (err) {
    dispose()
    rethrowAsTimeoutIfAbort(err)
  }
}

/**
 * POST JSON to an auth endpoint with the standard request timeout, returning
 * the raw Response. Unlike request(), it does NOT redirect on 401 — the login
 * hooks must surface 401/429 to the user instead of bouncing to /login (where
 * they already are). Throws 'Request timed out' on timeout / the raw fetch
 * error on network failure; callers map those to their own error type.
 *
 * The timeout covers the headers fetch; the few-byte login body read is left
 * untimed (covering it would push dispose() back into callers — the boilerplate
 * this helper exists to remove).
 */
export async function authPost(path: string, body: unknown): Promise<Response> {
  const { res, dispose } = await fetchWithTimeout(`${BASE_URL}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  dispose()
  return res
}

/** Deduplicate concurrent refresh attempts — only one in-flight at a time. */
let refreshPromise: Promise<boolean> | null = null

async function tryRefresh(): Promise<boolean> {
  if (refreshPromise) return refreshPromise
  refreshPromise = (async () => {
    try {
      const { res, dispose } = await fetchWithTimeout(`${BASE_URL}/api/v1/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      })
      dispose()
      return res.ok
    } catch {
      return false
    } finally {
      refreshPromise = null
    }
  })()
  return refreshPromise
}

/** Redirect to login without server call — safe for cascade 401s. */
export function forceRedirectToLogin(): never {
  window.location.href = loginRedirectTarget()
  return new Promise<never>(() => {}) as never
}

/** Map backend error details to safe user-facing messages. */
function safeErrorMessage(status: number, body: Record<string, unknown>): string {
  // Known user-facing errors from backend (DomainError subclasses)
  if (typeof body.detail === 'string') {
    // Only pass through short, non-technical messages (no SQL, no tracebacks)
    if (body.detail.length < 200 && !/sql|traceback|internal|exception/i.test(body.detail)) {
      return body.detail
    }
  }
  // Pydantic validation errors — extract just the messages
  if (Array.isArray(body.detail)) {
    const msgs = body.detail
      .map((e: { msg?: string }) => e.msg)
      .filter(Boolean)
      .join(', ')
    if (msgs) return msgs
  }
  // Generic fallbacks
  if (status === 403) return 'Access denied'
  if (status === 404) return 'Not found'
  if (status === 409) return 'Conflict — resource already exists'
  if (status === 422) return 'Invalid input'
  if (status >= 500) return 'Server error — please try again'
  return 'Something went wrong'
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  }

  // consumeBody centralises AbortError mapping for body reads — without it
  // a timeout that fires after headers arrive surfaces as DOMException
  // 'AbortError' from res.json() / res.text() and escapes to the browser's
  // unhandledrejection handler (Sentry PIXELLAB-WEB-3).
  async function consumeBody<U>(reader: () => Promise<U>): Promise<U> {
    try {
      return await reader()
    } catch (err) {
      rethrowAsTimeoutIfAbort(err)
    }
  }

  const { res, dispose } = await fetchWithTimeout(`${BASE_URL}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  })

  try {
    if (res.status === 401) {
      dispose()
      const refreshed = await tryRefresh()
      if (refreshed) {
        const { res: retry, dispose: disposeRetry } = await fetchWithTimeout(`${BASE_URL}${path}`, {
          ...options,
          headers,
          credentials: 'include',
        })
        try {
          if (retry.status === 401) {
            return forceRedirectToLogin()
          }
          if (retry.status === 204) return undefined as T
          if (!retry.ok) {
            const body = await consumeBody(() => retry.json().catch(() => ({})))
            throw new Error(safeErrorMessage(retry.status, body))
          }
          return await consumeBody(() => retry.json())
        } finally {
          disposeRetry()
        }
      }
      return forceRedirectToLogin()
    }

    if (!res.ok) {
      const body = await consumeBody(() => res.json().catch(() => ({})))
      throw new Error(safeErrorMessage(res.status, body))
    }

    if (res.status === 204) return undefined as T
    return await consumeBody(() => res.json())
  } finally {
    dispose()
  }
}

/** Explicit logout — POST to clear server cookies, clear device ID, redirect. */
export async function logout(): Promise<void> {
  try {
    const { dispose } = await fetchWithTimeout(`${BASE_URL}/api/v1/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    })
    dispose()
  } catch {
    // Logout is best-effort — redirect anyway
  }
  // Clear device identity so next login gets a fresh UUID.
  // Without this, relay sees same deviceID → Case C (same device) →
  // silent replace without session_replaced, potentially showing
  // stale call state from previous session.
  clearDeviceId()
  window.location.href = loginRedirectTarget()
}

/** Get a short-lived token for WebSocket auth. */
export async function getWsToken(): Promise<string> {
  const data = await request<{ token: string }>('/api/v1/auth/ws-token', { method: 'POST' })
  return data.token
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'POST',
      ...(body !== undefined && { body: JSON.stringify(body) }),
    }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: (path: string) => request<void>(path, { method: 'DELETE' }),
}
